// ============================================================================
// AnalysisDataContext
// Raw-dataset cache for the Analysis section. Coarser than
// ReservationDataContext's chunk cache: analysis ranges are month/season
// scale, so we cache one covering window of reservations and widen it as
// the manager expands the range. Mounted only inside AnalysisRoutes so the
// memory cost is paid only while the Analysis section is open.
//
// Correctness notes (see Documents/202607-ANALYSIS_DASHBOARD_DESIGN.md):
// - GET /reservations/ filters by check_in only, so every fetch applies a
//   LOOKBACK_DAYS buffer to capture stays that started before the window
//   and overlap it; the pure metrics clip site-nights to the window.
// - Fetched with active_only=false: cancelled and superseded rows are
//   needed by the cancellation/anomaly views; revenue/occupancy math
//   filters them out via isRevenueStay().
// ============================================================================

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getReservations } from '../services/reservationService';
import { fetchPayments, PaymentRecord } from '../services/paymentService';
import { getSites, getStoreSiteTypes, getStoreOverageFees } from '../services/siteService';
import { getAllActiveSiteBlackouts, getStoreBlackouts } from '../services/blackoutService';
import { getMembers, ApiMember } from '../services/memberService';
import { authService } from '../services/auth/authService';
import { Site, StoreOverageFee, StoreSiteType } from '../types/site';
import { AnalysisStay } from '../utils/analytics/types';
import { normalizeReservations } from '../utils/analytics/normalize';
import { BlackoutRange } from '../utils/analytics/occupancyMetrics';
import { addDays } from '../utils/dateUtils';

const LOOKBACK_DAYS = 60;

export interface AnalysisDataContextValue {
  /** Normalized reservations covering at least [from − 60d, to]. */
  stays: AnalysisStay[];
  sites: Site[];
  siteTypes: StoreSiteType[];
  blackouts: BlackoutRange[];
  payments: PaymentRecord[];
  members: ApiMember[];
  overageFees: StoreOverageFee[];
  loadedWindow: { from: string; to: string } | null;
  loading: boolean;
  error: string | null;
  /** Make sure reservations covering [from, to] are loaded. */
  ensureRange: (from: string, to: string) => void;
  refresh: () => void;
}

const AnalysisDataContext = createContext<AnalysisDataContextValue | undefined>(undefined);

export const AnalysisDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stays, setStays] = useState<AnalysisStay[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteTypes, setSiteTypes] = useState<StoreSiteType[]>([]);
  const [blackouts, setBlackouts] = useState<BlackoutRange[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [members, setMembers] = useState<ApiMember[]>([]);
  const [overageFees, setOverageFees] = useState<StoreOverageFee[]>([]);
  const [loadedWindow, setLoadedWindow] = useState<{ from: string; to: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The window actually fetched (includes the look-back buffer)
  const fetchedWindowRef = useRef<{ from: string; to: string } | null>(null);
  const requestIdRef = useRef(0);
  const staticsLoadedRef = useRef(false);

  const loadStatics = useCallback(async () => {
    const storeId = authService.getStoreId();
    const [siteList, typeList, storeBlackouts, siteBlackouts, paymentList, memberList, feeList] =
      await Promise.all([
        getSites(storeId),
        getStoreSiteTypes(storeId),
        getStoreBlackouts(storeId, true),
        getAllActiveSiteBlackouts(),
        storeId ? fetchPayments({ storeId }) : Promise.resolve([]),
        getMembers(),
        getStoreOverageFees(storeId),
      ]);
    setSites(siteList);
    setSiteTypes(typeList);
    setBlackouts([
      ...storeBlackouts.map(b => ({ startDate: b.start_date, endDate: b.end_date })),
      ...siteBlackouts.map(b => ({ siteId: b.site_id, startDate: b.start_date, endDate: b.end_date })),
    ]);
    setPayments(paymentList);
    setMembers(memberList);
    setOverageFees(feeList);
  }, []);

  const loadWindow = useCallback(async (from: string, to: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetchFrom = addDays(from, -LOOKBACK_DAYS);
      const tasks: Promise<void>[] = [
        getReservations({
          storeId: authService.getStoreId(),
          checkInFrom: fetchFrom,
          checkInTo: to,
          activeOnly: false,
        }).then(apiReservations => {
          if (requestId !== requestIdRef.current) return;
          setStays(normalizeReservations(apiReservations));
          fetchedWindowRef.current = { from: fetchFrom, to };
          setLoadedWindow({ from, to });
        }),
      ];
      if (!staticsLoadedRef.current) {
        staticsLoadedRef.current = true;
        tasks.push(loadStatics());
      }
      await Promise.all(tasks);
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load analysis data');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [loadStatics]);

  const ensureRange = useCallback((from: string, to: string) => {
    const fetched = fetchedWindowRef.current;
    const neededFrom = addDays(from, -LOOKBACK_DAYS);
    if (fetched && fetched.from <= neededFrom && fetched.to >= to) {
      // Cache hit — just update the logical window
      setLoadedWindow(current =>
        current && current.from === from && current.to === to ? current : { from, to }
      );
      return;
    }
    // Widen to the union so switching back never refetches
    const unionFrom = fetched && fetched.from < neededFrom
      ? addDays(fetched.from, LOOKBACK_DAYS) < from ? addDays(fetched.from, LOOKBACK_DAYS) : from
      : from;
    const unionTo = fetched && fetched.to > to ? fetched.to : to;
    loadWindow(unionFrom < from ? unionFrom : from, unionTo);
  }, [loadWindow]);

  const refresh = useCallback(() => {
    const window = loadedWindow;
    fetchedWindowRef.current = null;
    staticsLoadedRef.current = false;
    if (window) loadWindow(window.from, window.to);
  }, [loadedWindow, loadWindow]);

  // Reset the cache if the store changes mid-session
  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  const value: AnalysisDataContextValue = {
    stays,
    sites,
    siteTypes,
    blackouts,
    payments,
    members,
    overageFees,
    loadedWindow,
    loading,
    error,
    ensureRange,
    refresh,
  };

  return (
    <AnalysisDataContext.Provider value={value}>
      {children}
    </AnalysisDataContext.Provider>
  );
};

export function useAnalysisData(): AnalysisDataContextValue {
  const context = useContext(AnalysisDataContext);
  if (!context) {
    throw new Error('useAnalysisData must be used within an AnalysisDataProvider');
  }
  return context;
}
